import { useEffect, useState } from "react";
import { useConnectivityStore } from "../store/connectivityStore";
import { formatRelativeTime } from "../utils/format";

const REFRESH_MS = 60_000;

/**
 * Whether the app is offline, plus a sentence naming when the on-device copy
 * was saved. The sentence refreshes every minute while offline.
 */
function useOfflineNotice() {
  const mode = useConnectivityStore((s) => s.mode);
  const savedAt = useConnectivityStore((s) => s.savedAt);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (mode !== "offline") return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [mode]);

  return {
    offline: mode === "offline",
    copyText: savedAt
      ? `Viewing the copy saved ${formatRelativeTime(savedAt, now)}.`
      : "Viewing the copy saved on this device.",
  };
}

/** Persistent notice that the app is showing the on-device copy read-only. */
export function OfflineBanner() {
  const { offline, copyText } = useOfflineNotice();

  if (!offline) return null;

  return (
    <div
      role="status"
      data-testid="offline-banner"
      className="flex shrink-0 items-center justify-center gap-2 border-b border-border bg-warning-bg-strong px-4 py-2.5 text-center text-sm text-primary"
    >
      <span
        className="material-symbols-outlined text-base text-warning"
        aria-hidden="true"
      >
        cloud_off
      </span>
      <span>
        <span className="font-semibold">Offline.</span> {copyText} Editing is
        turned off.
      </span>
    </div>
  );
}

/**
 * Compact header-row form of {@link OfflineBanner} for the phone trip board,
 * where it takes the save indicator's place. The full sentence stays
 * available to assistive tech and as a tooltip.
 */
export function OfflineStatusChip() {
  const { offline, copyText } = useOfflineNotice();

  if (!offline) return null;

  const description = `Offline. ${copyText} Editing is turned off.`;

  return (
    <div
      role="status"
      data-testid="offline-status-chip"
      title={description}
      className="flex shrink-0 items-center gap-1 rounded-md bg-warning-bg-strong px-2 py-1 text-xs font-semibold text-primary"
    >
      <span
        className="material-symbols-outlined text-base text-warning"
        aria-hidden="true"
      >
        cloud_off
      </span>
      <span aria-hidden="true">Offline</span>
      <span className="sr-only">{description}</span>
    </div>
  );
}
